import React, { createContext, useContext, useState, useCallback } from 'react';
import RateLimitSnackbar from '../components/RateLimitSnackbar';

const RateLimitContext = createContext();

export const useRateLimit = () => {
    const context = useContext(RateLimitContext);
    if (!context) {
        throw new Error('useRateLimit must be used within a RateLimitProvider');
    }
    return context;
};

export const RateLimitProvider = ({ children }) => {
    const [rateLimitState, setRateLimitState] = useState({
        isOpen: false,
        message: '',
        retryAfter: null,
        onRetry: null
    });

    const showRateLimit = useCallback((error) => {
        // Prevent showing multiple rate limit notifications
        if (rateLimitState.isOpen) {
            return;
        }

        const message = error.userMessage || 'Too many requests. Please wait before trying again.';
        const retryAfter = error.retryAfter;

        setRateLimitState({
            isOpen: true,
            message,
            retryAfter,
            onRetry: null // Can be enhanced later for specific retry actions
        });
    }, [rateLimitState.isOpen]);

    const hideRateLimit = useCallback(() => {
        setRateLimitState(prev => ({
            ...prev,
            isOpen: false
        }));
    }, []);

    const handleRetry = useCallback(() => {
        if (rateLimitState.onRetry) {
            rateLimitState.onRetry();
        }
        hideRateLimit();
    }, [rateLimitState.onRetry, hideRateLimit]);

    const value = {
        showRateLimit,
        hideRateLimit,
        isRateLimited: rateLimitState.isOpen
    };

    return (
        <RateLimitContext.Provider value={value}>
            {children}
            <RateLimitSnackbar
                open={rateLimitState.isOpen}
                onClose={hideRateLimit}
                message={rateLimitState.message}
                retryAfter={rateLimitState.retryAfter}
                onRetry={rateLimitState.onRetry ? handleRetry : null}
            />
        </RateLimitContext.Provider>
    );
};

export default RateLimitContext;